import { AxiosError } from 'axios';

/**
 * Standardized API error class that maps to the backend error structure
 */
export class WeTreeApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'WeTreeApiError';
  }

  /**
   * Creates a WeTreeApiError from an Axios error response
   */
  static fromAxiosError(error: AxiosError): WeTreeApiError {
    // Handle API error responses with our standard format: { error: { code, message, details? } }
    if (error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data) {
      const apiError = (error.response.data as { error: { code?: string; message?: string; details?: unknown } }).error;
      return new WeTreeApiError(
        apiError.code || 'UNKNOWN_API_ERROR',
        apiError.message || error.message,
        error.response.status,
        apiError.details,
        error
      );
    }

    // Handle network errors, timeouts, etc.
    if (!error.response) {
      return new WeTreeApiError(
        'NETWORK_ERROR',
        error.message || 'Network request failed',
        0,
        {
          code: error.code,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL
          }
        },
        error
      );
    }

    // Handle non-standard API responses (shouldn't happen with our API, but good fallback)
    return new WeTreeApiError(
      'HTTP_ERROR',
      `HTTP ${error.response.status}: ${error.response.statusText}`,
      error.response.status,
      { responseData: error.response.data },
      error
    );
  }

  /**
   * Check if this is a specific error type
   */
  is(code: string): boolean {
    return this.code === code;
  }

  /**
   * Check if this is a validation error
   */
  isValidationError(): boolean {
    return this.code === 'VALIDATION_ERR';
  }

  /**
   * Check if this is an authorization error
   */
  isAuthError(): boolean {
    return this.code === 'UNAUTHORIZED' || this.code === 'FORBIDDEN';
  }

  /**
   * Check if this is a network error (no response from server)
   */
  isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR';
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      case 'UNAUTHORIZED':
        return 'You are not authorized to perform this action. Please sign in and try again.';
      case 'FORBIDDEN':
        return 'You do not have permission to perform this action.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'VALIDATION_ERR':
        return this.message; // Validation messages are usually user-friendly
      case 'CONFLICT':
        return this.message; // Conflict messages are usually user-friendly
      case 'FEATURE_REQUIRES_UPGRADE':
        return this.message || 'This feature requires the Family plan. Upgrade to unlock video and audio uploads.';
      case 'LIMIT_REACHED':
        return this.message || 'You have reached a plan limit. Upgrade for higher limits.';
      case 'DATABASE_ERR':
        return 'A server error occurred. Please try again later.';
      default:
        return this.message || 'An unexpected error occurred. Please try again.';
    }
  }

  /** True when the API indicates an upgrade is needed (feature gate or quota). */
  isUpgradeRequired(): boolean {
    if (this.code === 'FEATURE_REQUIRES_UPGRADE' || this.code === 'LIMIT_REACHED') {
      return true;
    }
    const details = this.details as { upgradeRequired?: boolean } | undefined;
    return details?.upgradeRequired === true;
  }
}

/**
 * Result type for API operations - either success with data or failure with error
 */
export type ApiResult<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: WeTreeApiError;
    };

/**
 * Configuration error for when API setup fails
 */
export class ConfigurationError extends WeTreeApiError {
  constructor(message: string = 'Failed to get API configuration') {
    super('CONFIG_ERROR', message, 0);
  }
}

/**
 * Common error codes from the backend
 */
export const ErrorCodes = {
  DATABASE_ERR: 'DATABASE_ERR',
  MISSING_IDENTITY: 'MISSING_IDENTITY',
  NO_ACCESS: 'NO_ACCESS',
  VALIDATION_ERR: 'VALIDATION_ERR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  CONFLICT: 'CONFLICT',
  FEATURE_REQUIRES_UPGRADE: 'FEATURE_REQUIRES_UPGRADE',
  LIMIT_REACHED: 'LIMIT_REACHED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONFIG_ERROR: 'CONFIG_ERROR'
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];
